import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Grid, Button } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import { useSelector, useDispatch } from "react-redux";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { mentoringAvailabilitySchema } from "app/components/mvp/formWidget/model/mentoringAvailabilityJson";
import { mentoringAvailabilityUiSchema } from "app/components/uiSchema/mentoringAvailabilityUiSchema";
import { MentoringAvailabilityPayload } from "app/services/payloadSubmit/mentoringAvailabilityPayload";
import { config } from "utilities/config";
import { post } from "app/services/api";
import FormWidget from "app/components/mvp/formWidget";
import TabWidget from "app/components/molecules/tab/TabWidget.mui";
import { globalTheme } from "style/globalTheme";
import { useStyles } from "./style";
import ListWidget from "app/components/mvp/listWidget";
import { AddTopicPayload, DeleteTopicPayload } from "./model/topicsPayload";
import { getMentoringTopicsListSchema } from "./model/mentoringTopicsSchema";
import { getTopicsListUiSchema } from "./model/topicsUiSchema";

function MentoringManageAvailability(props) {
  const classes = useStyles();
  const userEntitlement = useSelector((state) => state.entitleMents);
  const userRole = useSelector((state) => state.role);
  let permissions =
    userEntitlement && userEntitlement.find((x) => x.route == props.match.path);
  const contactid = useSelector((state) => state.contactid);
  const [availabilityFormData, setAvailabilityFormData] = useState({});
  const [topicsData, setTopicsData] = useState([]);
  const [selectedTab, setSelectedTab] = useState(1);
  // Use ref to store current form data without causing re-renders
  const currentFormDataRef = useRef({});
  const dispatch = useDispatch();

  // Memoize availability object to prevent unnecessary re-renders
  // Only use state for initial form data - FormWidget manages its own state during typing
  const availability = useMemo(() => ({
    widgetType: "updateContact",
    formSchema: mentoringAvailabilitySchema,
    uiSchema: mentoringAvailabilityUiSchema,
    formData: availabilityFormData, // Only used for initial load
    apiURL: config.teleportal.submitForm.Update,
    payloadSchema: {},
    entityType: "contacts",
    formUischema: "",
    button1Label: "Save",
    button2Label: "Go Back",
    payloadSubmit: MentoringAvailabilityPayload,
    contactId: contactid,
    goBackPage: "/",
    button2GoBack: true,
    historyUrl: "",
  }), [availabilityFormData, contactid]);


  useEffect(() => {
    if (contactid.length > 0) {
      getAvailabilityDetails();
      getTopicsData();
    }
  }, [contactid]);

  const getTopicsData = () => {
    if (!contactid || contactid.length === 0) {
      return;
    }

    const api = config.teleportal.mentoring.listTopicsFromContacts;
    const payload = {
      contactid: contactid,
    };

    post(api, payload)
      .then((res) => {
        if (Array.isArray(res.data)) {
          // Log first item to see structure
          
          // Add unique id to each row for DataGrid and ensure ctpt_topicsid is preserved
          const dataWithIds = res.data.map((item, index) => {
            // Try to find the actual topic ID from various possible field names
            // The FetchXML uses alias "ac" for the linked topic entity
            const topicId = item.ctpt_topicsid || 
                           item["ac.ctpt_topicsid"] ||
                           item["ac_ctpt_topicsid"] ||
                           item._ctpt_topicsid_value ||
                           item["ctpt_topicsid@OData.Community.Display.V1.FormattedValue"] ||
                           // Check all keys that might contain the topic ID
                           (() => {
                             const keys = Object.keys(item);
                             const idKey = keys.find(key => 
                               key.toLowerCase().includes('topicsid') || 
                               (key.toLowerCase().includes('topicid') && !key.toLowerCase().includes('contact'))
                             );
                             return idKey ? item[idKey] : null;
                           })() ||
                           item.id ||
                           null;
            
            // Use topicId if it's a GUID, otherwise generate a unique ID
            const rowId = topicId && topicId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) 
                          ? topicId 
                          : (item.id || `topic-${index}`);
            
            return {
              ...item,
              id: rowId,
              // Ensure ctpt_topicsid is available for deletion - preserve the actual GUID if found
              ctpt_topicsid: topicId || item.ctpt_topicsid || item._ctpt_topicsid_value,
            };
          });
          setTopicsData(dataWithIds);
        } else {
          setTopicsData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching topics data:", error);
        setTopicsData([]);
      });
  };

  // Memoize updateFormData to prevent unnecessary re-renders
  // Store form data in ref instead of state to prevent flickering during typing
  // FormWidget manages its own internal state, so we don't need to sync on every keystroke
  const updateFormData = useCallback((entityType, formData) => {
    // Store in ref to avoid triggering re-renders during typing
    currentFormDataRef.current = formData;
    // Don't update state here - it causes the availability object to be recreated
    // which triggers FormWidget re-renders and causes flickering
  }, []);

  const getAvailabilityDetails = () => {
    let api;
    let payloads;
    const formData = {};
    api = config.teleportal.submitForm.getId;

    payloads = {
      properties: [
        "contactid",
        "ctpt_optedintosaiceconnect",
        "ctpt_availabledaytime",
        "ctpt_availableevenings",
        "ctpt_availableonweekdays",
        "ctpt_availableonweekends",
        "ctpt_availabilitynotes",
        "ctpt_connectmentor",
      ],
      formattedFields: [],
      recordType: "contacts",
      id: contactid,
    };


    post(api, payloads)
      .then((res) => {
        res.data.data.forEach((x) => {
          formData[x.name] = x.value;
        });
        // Store current form data in ref to keep it stable
        currentFormDataRef.current = { ...formData };
        // Update state only on initial load - this won't cause flicker
        setAvailabilityFormData(formData);
      })
      .catch((error) => {
        console.error("Error fetching availability details:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load availability details",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  };

  const getResponse = (res) => {
    if (res.data.id) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Availability updated successfully",
          code: "",
          display: "show",
          severity: "success",
        },
      });
      // Refresh availability data after successful update
      if (contactid.length > 0) {
        getAvailabilityDetails();
      }
    } else {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Failed to update availability",
          code: "",
          display: "show",
          severity: "error",
        },
      });
    }
  };

  // Columns for Topics tab (matches ListTopicsfromContactsAsync response format)
  const topicsColumns = [
    {
      field: "topicName",
      flex: 1,
      headerName: "Topic",
      headerClassName: "super-app-theme--header",
    },
    // {
    //   field: "description",
    //   flex: 1.5,
    //   headerName: "Description",
    //   headerClassName: "super-app-theme--header",
    // },
    // {
    //   field: "category",
    //   flex: 1,
    //   headerName: "Category",
    //   headerClassName: "super-app-theme--header",
    // },
  ];

  // Configuration for Topics Grid
  const topicsConfiguration = {
    apiURL: config.teleportal.mentoring.listTopicsFromContacts,
    apiUrlDel: config.teleportal.submitForm.Update,
    columns: topicsColumns,
    searchPayload: "searchFilter",
    getlistPayload: {},
    deleteListPayload: DeleteTopicPayload,
    formwidgetOptions: {
      widgetType: "update",
      formSchema: getMentoringTopicsListSchema,
      uiSchema: getTopicsListUiSchema,
      // Many-to-many link between Contact and Topic is an UPDATE operation
      apiURL: config.teleportal.submitForm.Create,
      entityType: "ctpt_topics",
      formUischema: "",
      button1Label: "Add",
      button2Label: "Cancel",
      button3Label: "Clear",
      payloadSubmit: AddTopicPayload,
      columns: topicsColumns,
      contactId: contactid,
      historyUrl: "",
    },
    listHeading: "",
    nonRecord: false,
    role: "",
    rows: "",
    localDataStore: false,
    hideControls: false,
    hideAdd: false,
    hasAggregate: false,
    staticTable: true,
    staticRow: topicsData,
    formatStaticRow: false,
    hiddenColumn: {},
    contactId: contactid,
    filteredValue: "",
    hasFilter: false,
    filterField: "",
    filterValue: [],
    aggregateColumns: [],
    aggregateField: "",
    hasPayloadfilter: false,
    hideDelete: false,
    hideSearch: true,
    hideFooterPagination: true,
    enableLocalSearch: true,
    hideModalOnSubmit: true,
    pageSize: 50,
    getRowId: (row) => row.id,
    // After adding a topic, refresh the static rows from the dedicated API
    onRecordEdit: getTopicsData,
  };


  return (
    <ThemeProvider theme={globalTheme}>
      <AuthenticatedTemplate>
        <div className={classes.container}>
          <TabWidget
            value={selectedTab}
            onChange={setSelectedTab}
            options={{
              defaultTabID: 1,
              tabNavJson: [
              {
                label: "Manage Availability",
                id: 1,
                entity: (
                  <Grid container spacing={0} justify="center">
                    <Grid item xs={12} md={9}>
                      <FormWidget
                        options={availability}
                        updateFormData={updateFormData}
                        successResponse={getResponse}
                      />
                    </Grid>
                  </Grid>
                ),
              },
              {
                label: "Topics",
                id: 2,
                entity: (
                  <Grid container spacing={0} justify="center">
                    <Grid item xs={12} md={10} className={classes.gridContainer}>
                      <ListWidget options={topicsConfiguration} />
                    </Grid>
                  </Grid>
                ),
              },
            ],
          }}
          />
        </div>
        {selectedTab === 2 && (
          <div style={{ 
            width: "100%", 
            marginTop: "-24px",
            paddingLeft: "2rem", 
            paddingRight: "2rem",
            position: "relative",
            zIndex: 10
          }}>
            <Grid container spacing={0} justify="center">
              <Grid item xs={12} md={10} style={{ paddingLeft: "16px" }}>
                <Button
                  variant="text"
                  onClick={() => setSelectedTab(1)}
                  className={classes.goBackButton}
                >
                  Go Back
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
      </AuthenticatedTemplate>
    </ThemeProvider>
  );
}

export default MentoringManageAvailability;
